import React from 'react'
import { useT } from '../../lib/i18n'
import { useApp } from '../../lib/context'
import { useAggregate, exportCsvUrl, type Bucket } from '../../lib/api'

const METRICS: { key: keyof Bucket['totals']; label: string; redIfHigh?: boolean }[] = [
  { key: 'actions',      label: 'actions' },
  { key: 'errors',       label: 'errors', redIfHigh: true },
  { key: 'inProgress',   label: 'inprogress' },
  { key: 'completed',    label: 'completed' },
  { key: 'successRate',  label: 'success_rate' },
  { key: 'commits',      label: 'commits' },
  { key: 'filesModified',label: 'files_mod' },
  { key: 'timeSeconds',  label: 'time_spent' },
]

export default function Compare() {
  const t = useT()
  const { period } = useApp()
  const { data, loading } = useAggregate(period)

  const buckets = data?.buckets ?? []

  if (loading && !data) return (
    <div style={{ padding: '24px', color: 'var(--text-muted)' }}>{t('loading')}</div>
  )

  const projectIds = buckets.map((b) => b.projectId)
  const bMap = new Map(buckets.map((b) => [b.projectId, b]))

  function getValue(pid: string, key: keyof Bucket['totals']): number {
    return bMap.get(pid)?.totals[key] ?? 0
  }

  return (
    <div style={{ overflowX: 'auto', overscrollBehaviorX: 'contain' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
        <a
          href={exportCsvUrl(period)}
          download
          style={{
            padding: '5px 12px',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            background: 'var(--surface)',
            color: 'var(--text-muted)',
            fontSize: '12px',
            textDecoration: 'none',
            fontFamily: 'var(--font-sans)',
          }}
        >
          ↧ {t('export_csv')}
        </a>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '600px' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)' }}>
            <th style={{ ...thStyle, width: '120px' }}>{t('type')}</th>
            {projectIds.map((pid) => (
              <th key={pid} style={{ ...thStyle, textAlign: 'right', maxWidth: '120px' }}>
                <span style={{ fontSize: '10px', fontFamily: 'var(--font-mono)' }}>{pid}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {METRICS.map(({ key, label, redIfHigh }) => {
            const values = projectIds.map((pid) => getValue(pid, key))
            const max = Math.max(...values, 1)

            return (
              <tr key={key} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ ...tdStyle, color: 'var(--text-muted)', fontWeight: 500 }}>
                  {t(label)}
                </td>
                {projectIds.map((pid, i) => {
                  const val = values[i] ?? 0
                  const pct = Math.round((val / max) * 100)
                  const isMax = val === max && max > 0
                  const isRed = redIfHigh && val > 0
                  return (
                    <td key={pid} style={{ ...tdStyle, textAlign: 'right' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end' }}>
                        <div style={{
                          height: '6px',
                          width: `${Math.max(pct, val > 0 ? 4 : 0)}%`,
                          maxWidth: '80px',
                          background: isRed ? 'var(--health-crit)' : 'var(--accent)',
                          opacity: 0.6,
                          borderRadius: '2px',
                          transition: 'width 300ms',
                        }} />
                        <span style={{
                          fontFamily: 'var(--font-mono)',
                          fontWeight: isMax ? 700 : 400,
                          color: isRed ? 'var(--health-crit)' : isMax ? 'var(--text)' : 'var(--text-muted)',
                          minWidth: '36px',
                          textAlign: 'right',
                        }}>
                          {key === 'successRate' ? `${val}%` : key === 'timeSeconds' ? `${Math.round(val / 60)}m` : val}
                        </span>
                      </div>
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>

      {buckets.length === 0 && (
        <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>
          {t('no_data')}
        </div>
      )}
    </div>
  )
}

const thStyle: React.CSSProperties = {
  padding: '8px', textAlign: 'left', fontSize: '11px',
  fontWeight: 600, color: 'var(--text-muted)',
  textTransform: 'uppercase', letterSpacing: '0.05em',
}
const tdStyle: React.CSSProperties = { padding: '7px 8px', verticalAlign: 'middle' }
